import React, {
  useState,
  useRef,
  useCallback,
  forwardRef,
  useImperativeHandle,
  useEffect,
} from 'react'
import cx from 'classnames'
import isEqual from 'lodash/isEqual'
import { useAppDispatch } from 'reduxTyped'
import { BtnV2, Icon, l } from '@manychat/manyui'

import { StartFlowAction } from 'apps/aiAutomation/components/EngagementGoalEditor/components/LeadQualEditor/components/ExitActionEditor/components/StartFlowAction/StartFlowAction'
import { ConfigFormValues } from 'apps/aiAutomation/components/EngagementGoalEditor/components/LeadQualEditor/lib/types'
import { useGoalEditorContext } from 'apps/aiAutomation/components/EngagementGoalEditor/lib/context'
import {
  GoalEditorMode,
  SubmitRef,
} from 'apps/aiAutomation/components/EngagementGoalEditor/lib/types'
import { AiAutomationLayoutActions } from 'apps/aiAutomation/store/layout'
import {
  ExitActionType,
  GoalType,
  LeadQualExitAction,
} from 'shared/api/requests/aiEngagementGoal/schemas'

import sharedCm from '../../../EditorLayout/EditorLayout.module.css'

import { AssignConversationAction } from './components/AssignConversationAction/AssignConversationAction'
import { SendMessageAction } from './components/SendMessageAction/SendMessageAction'

import cm from './ExitActionEditor.module.css'

interface ExitActionEditorProps {
  isActive: boolean
  configData: ConfigFormValues | null
  exitActionData: LeadQualExitAction | null

  onFinish: (action: LeadQualExitAction | null) => void
}

export const ExitActionEditor = forwardRef<
  SubmitRef<LeadQualExitAction | null>,
  ExitActionEditorProps
>(({ isActive, configData, exitActionData, onFinish }, ref) => {
  const dispatch = useAppDispatch()

  const actionEditorRef = useRef<SubmitRef<LeadQualExitAction>>(null)
  const { mode } = useGoalEditorContext(GoalType.LEAD_QUAL)
  const [selectedType, setSelectedType] = useState<ExitActionType | null>(
    exitActionData ? exitActionData.type : null,
  )

  const onFinishExitAction = useCallback(async () => {
    if (selectedType === null) {
      onFinish(null)
      return
    }

    if (!actionEditorRef?.current) {
      return
    }

    const result = await actionEditorRef.current.submit()

    if (result.isValid) {
      onFinish(result.data)
    }
  }, [onFinish, selectedType])

  useEffect(() => {
    if (isActive) {
      const hasChanges = !isEqual(exitActionData ? exitActionData.type : null, selectedType)
      dispatch(AiAutomationLayoutActions.showConfirmChanged(hasChanges))
    } else {
      dispatch(AiAutomationLayoutActions.showConfirmChanged(false))
    }
  }, [dispatch, exitActionData, isActive, selectedType])

  useImperativeHandle(
    ref,
    () => ({
      submit: async () => {
        if (selectedType === null) {
          return { isValid: true, data: null }
        }

        if (!actionEditorRef.current) {
          return { isValid: false }
        }

        return await actionEditorRef.current.submit()
      },
    }),
    [selectedType],
  )

  return (
    <section>
      <div className={sharedCm.section}>
        <h4 className={cx('m-b-0 font-semibold', !isActive && 'text-secondary')}>
          {l.translate('Decide what happens next')}
        </h4>
        {configData && (
          <>
            <p className="m-t-xxs text-secondary">
              {l.translate(
                'Choose how the conversation should end. You’ll preview this before going live.',
              )}
            </p>

            <div className={cx(cm.actions, selectedType && 'm-b-lg')}>
              <label className={cm.actionOption}>
                <span className={cm.actionOption__header}>
                  <Icon.SequencesRemove className={cm.actionOption__icon} size={24} />
                </span>
                <span>{l.translate('I’d skip')}</span>

                <input
                  className={cm.actionOption__input}
                  name="action_type"
                  type="radio"
                  disabled={!isActive}
                  checked={selectedType === null}
                  onChange={() => setSelectedType(null)}
                />
              </label>

              <label className={cm.actionOption}>
                <span className={cm.actionOption__header}>
                  <Icon.OrphanAdsCampaignMessages className={cm.actionOption__icon} size={24} />
                </span>
                <span>{l.translate('Send a message')}</span>

                <input
                  className={cm.actionOption__input}
                  name="action_type"
                  type="radio"
                  disabled={!isActive}
                  checked={selectedType === ExitActionType.SEND_MESSAGE}
                  onChange={() => setSelectedType(ExitActionType.SEND_MESSAGE)}
                />
              </label>

              <label className={cm.actionOption}>
                <span className={cm.actionOption__header}>
                  <Icon.Flow className={cm.actionOption__icon} size={24} />
                </span>
                <span>{l.translate('Trigger a flow')}</span>

                <input
                  className={cm.actionOption__input}
                  name="action_type"
                  type="radio"
                  disabled={!isActive}
                  checked={selectedType === ExitActionType.START_FLOW}
                  onChange={() => setSelectedType(ExitActionType.START_FLOW)}
                />
              </label>

              <label className={cm.actionOption}>
                <span className={cm.actionOption__header}>
                  <Icon.Assign className={cm.actionOption__icon} size={24} />
                </span>
                <span>{l.translate('Assign to a person')}</span>

                <input
                  className={cm.actionOption__input}
                  name="action_type"
                  type="radio"
                  disabled={!isActive}
                  checked={selectedType === ExitActionType.ASSIGN_CONVERSATION}
                  onChange={() => setSelectedType(ExitActionType.ASSIGN_CONVERSATION)}
                />
              </label>
            </div>
            {selectedType === ExitActionType.ASSIGN_CONVERSATION && (
              <AssignConversationAction
                isActive={isActive}
                ref={actionEditorRef}
                exitActionData={exitActionData}
              />
            )}
            {selectedType === ExitActionType.SEND_MESSAGE && (
              <SendMessageAction
                isActive={isActive}
                ref={actionEditorRef}
                exitActionData={exitActionData}
              />
            )}
            {selectedType === ExitActionType.START_FLOW && (
              <StartFlowAction
                isActive={isActive}
                ref={actionEditorRef}
                exitActionData={exitActionData}
              />
            )}
          </>
        )}
      </div>

      {isActive && mode === GoalEditorMode.CREATE && (
        <div className="d-flex justify-end m-b-lg">
          <BtnV2 onClick={onFinishExitAction} className="m-t-sm">
            {l.translate('Next')}
          </BtnV2>
        </div>
      )}
    </section>
  )
})
